import React, { useEffect } from 'react';
import { useApparelShop } from '@/hooks/useApparelShop';
import ProductDetailsDialog from '@/components/apparel/shop/ProductDetailsDialog';
import CartSheet from '@/components/apparel/shop/CartSheet';
import CheckoutSheet from '@/components/apparel/shop/CheckoutDialog';
import { toast } from 'sonner';
import { PaymentService } from '@/services/payment';
import { useAuth } from '@/hooks/useAuth';
import { ShopLoadingState } from '@/components/apparel/shop/layout/ShopLoadingState';
import { ShopErrorState } from '@/components/apparel/shop/layout/ShopErrorState';
import { ProductGrid } from '@/components/apparel/shop/layout/ProductGrid';
import { motion } from 'framer-motion';
import { ShoppingBag, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';

const ApparelShop = () => {
  const { user } = useAuth();
  const organizationId = user?.user_metadata?.organization_id;
  
  const {
    products,
    loading,
    error,
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    updateSize,
    clearCart,
    selectedProduct,
    detailsOpen,
    setDetailsOpen,
    cartOpen,
    setCartOpen,
    checkoutOpen,
    setCheckoutOpen,
    handleProductSelect,
    cartItemCount,
    cartSubtotal
  } = useApparelShop();

  useEffect(() => {
    if (error) {
      console.error("Error fetching products:", error);
    }
  }, [error]);

  const handleCheckout = async () => {
    if (cartItemCount === 0) {
      toast.error("Your cart is empty");
      return;
    }
    
    if (!user?.email) {
      toast.error("Please log in to checkout");
      return;
    }
    
    try {
      const toastId = toast.loading("Preparing checkout...");
      
      // Create line items from cart
      const totalAmount = cartSubtotal();
      
      console.log("Starting checkout process", {
        amount: totalAmount / 100,
        items: cart.length,
        email: user.email,
        organizationId
      });
      
      const result = await PaymentService.createOneTimePayment({
        amount_cents: totalAmount, 
        currency: 'usd',
        name: 'Apparel Purchase',
        description: `${cartItemCount} item(s) from apparel shop`,
        customer_email: user.email,
        success_url: `${window.location.origin}/payment-success?source=apparel`,
        cancel_url: `${window.location.origin}/payment-canceled?source=apparel`,
        organization_id: organizationId, 
        isPlatformPayment: false,
        metadata: {
          user_id: user.id,
          organization_id: organizationId || '',
          cart_items: JSON.stringify(cart.map(item => ({
            product_id: item.product.id,
            quantity: item.quantity,
            size: item.size
          })))
        }
      });
      
      toast.dismiss(toastId);
      
      if (result?.url) {
        toast.success("Redirecting to secure checkout");
        console.log("Redirecting to Stripe checkout:", result.url);
        
        // Brief delay to allow toast to be seen
        setTimeout(() => {
          window.location.href = result.url;
        }, 500);
      } else if (result?.error) {
        throw new Error(`Payment service error: ${result.error}`);
      } else {
        throw new Error("Couldn't create checkout session - no URL returned");
      }
    } catch (error) {
      toast.dismiss();
      
      const errorMessage = error instanceof Error 
        ? error.message 
        : "Please try again or contact support";
      
      toast.error("Checkout failed", { 
        description: errorMessage
      });
      
      console.error("Checkout error:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-slate-600 font-medium">Loading shop...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return <ShopErrorState error={error} />;
  }

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-rose-600 px-8 py-8 text-white rounded-t-3xl -mx-8 -mt-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold mb-2">Apparel Shop</h1>
            <p className="text-purple-100 text-lg">
              Browse and purchase our exclusive apparel and merchandise
            </p>
          </div>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button 
              onClick={() => setCartOpen(true)}
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm border border-white/30 text-white px-6 py-3 rounded-xl font-semibold shadow-lg transition-all duration-300 relative"
            >
              <ShoppingCart className="h-5 w-5 mr-2" />
              <span>Cart</span>
              {cartItemCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-rose-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                  {cartItemCount}
                </span>
              )}
            </Button>
          </motion.div>
        </div>
      </div>
      
      <ProductGrid 
        products={products}
        onProductSelect={handleProductSelect}
      />

      {selectedProduct && (
        <ProductDetailsDialog
          product={selectedProduct}
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
          onAddToCart={addToCart}
        />
      )}
      
      <CartSheet 
        items={cart}
        open={cartOpen} 
        onOpenChange={setCartOpen}
        onRemoveItem={removeFromCart}
        onUpdateQuantity={updateQuantity}
        onUpdateSize={updateSize}
        onCheckout={handleCheckout}
        cartItems={cart}
        cartSubtotal={cartSubtotal}
      />
      
      {checkoutOpen && (
        <CheckoutSheet
          open={checkoutOpen}
          onOpenChange={setCheckoutOpen}
          cartItems={cart}
          families={[]} 
          students={[]} 
          onSuccess={() => {
            clearCart();
            setCheckoutOpen(false);
            toast.success("Order placed successfully!");
          }}
          onCancel={() => setCheckoutOpen(false)}
          onRemoveProduct={removeFromCart}
          onUpdateQuantity={updateQuantity}
          onUpdateSize={updateSize}
        />
      )}
    </div>
  );
};

export default ApparelShop;
